import { Game } from "../../core/Game";
import { Player } from "../entities/Player";
import { WorkStance } from "../../game/player-motion";
import { Vector } from "../../game/Vectors";

/**
 * Player input commands: the only way the dispatcher (and the driver)
 * touch the player's transient input state, and the one place the bench
 * dive's stance hold writes the body (standAtStance). Held-key state is
 * physical — pressed and released — so each command is a plain setter;
 * the entity's tick does the rest.
 */

function player(game: Game): Player {
  return game.entities.getSingleton(Player);
}

/** Steer the body. Cleared by passing [0, 0]. */
export function setMoveInput(game: Game, input: Vector): void {
  player(game).moveInput = [...input];
}

/** Press or release the operate key. */
export function setOperating(game: Game, operating: boolean): void {
  player(game).operating = operating;
}

/** Press or release the wait key. */
export function setWaiting(game: Game, waiting: boolean): void {
  player(game).waiting = waiting;
}

/** Aim the broom's swath, or null to fall back to the facing. */
export function setSweepAim(game: Game, aim: Vector | null): void {
  player(game).sweepAim = aim ? [...aim] : null;
}

/** How close counts as standing on the spot, in cells. */
const STANCE_SNAP_CELLS = 0.12;

/**
 * Hold the body at a station's working stance (the bench dive does this
 * every tick while it's open): square the facing to the top, steer the
 * walk toward the spot, and snap the last fraction of a step — a body
 * drifting a hair past the spot would wander for as long as the bench
 * is open. `playerChanged` goes out only when the body actually moved,
 * so the standing frames after it cost nothing.
 */
export function standAtStance(game: Game, stance: WorkStance): void {
  const thePlayer = player(game);
  const turned =
    thePlayer.direction !== stance.direction ||
    thePlayer.heading !== stance.heading;
  thePlayer.direction = stance.direction;
  thePlayer.heading = stance.heading;
  const dx = stance.pos[0] - thePlayer.position[0];
  const dy = stance.pos[1] - thePlayer.position[1];
  const distance = Math.hypot(dx, dy);
  if (distance < STANCE_SNAP_CELLS) {
    const landed = distance > 0;
    thePlayer.position = [...stance.pos];
    thePlayer.moveInput = [0, 0];
    if (turned || landed) game.dispatch("playerChanged", {});
    return;
  }
  thePlayer.moveInput = [dx / distance, dy / distance];
  if (turned) game.dispatch("playerChanged", {});
}
